import { useState, useCallback, useRef, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  Camera, 
  MessageSquare, 
  Eye, 
  Hand, 
  Mountain, 
  ArrowLeftRight,
  Trophy,
  Megaphone,
  MessageCircle,
  Theater,
  PartyPopper,
  Zap,
  Upload,
  Copy,
  Check,
  Trash2,
  Wand2,
  Film,
  Save,
  User,
  Sparkles
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  MOVEMENTS,
  MOVEMENT_CATEGORIES,
  MODES,
  NARRATION_STYLES,
  ACCENTS,
  INTENSITIES,
  LOADER_STEPS,
  CONTEXT_CONFIG,
  PERSONALITY_ARCHETYPES
} from "@/lib/movements";
import { HistoryPresetDrawer } from "@/components/HistoryPresetDrawer";
import { AuthButton } from "@/components/AuthButton";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const iconMap = {
  MessageSquare,
  Eye,
  Hand,
  Mountain,
  ArrowLeftRight,
  Trophy,
  Megaphone,
  MessageCircle,
  Theater,
  PartyPopper,
  Zap,
  User,
  Sparkles,
};

// AI Models configuration
const AI_MODELS = [
  { id: 'claude', name: 'Claude', provider: 'anthropic', model: 'claude-sonnet-4-6', color: '#F97316' },
  { id: 'gpt', name: 'GPT-5.2', provider: 'openai', model: 'gpt-5.2', color: '#10B981' },
  { id: 'gemini', name: 'Gemini', provider: 'gemini', model: 'gemini-2.5-flash', color: '#3B82F6' },
];

export default function ShotEngine({ user, onLogout }) {
  // State
  const [mode, setMode] = useState("dialogue");
  const [hook, setHook] = useState("");
  const [context, setContext] = useState("");
  const [accent, setAccent] = useState("guatemalan");
  const [intensity, setIntensity] = useState("dynamic");
  const [vfx, setVfx] = useState("off");
  const [narrationStyle, setNarrationStyle] = useState(NARRATION_STYLES[0]);
  const [selectedMovement, setSelectedMovement] = useState(null);
  const [movementCategory, setMovementCategory] = useState("push");
  const [selectedAI, setSelectedAI] = useState(AI_MODELS[0]);
  
  // Personality state
  const [personality, setPersonality] = useState(null);
  const [customPersonality, setCustomPersonality] = useState("");
  const [isEnhancingPersonality, setIsEnhancingPersonality] = useState(false);
  
  // Images
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageA, setImageA] = useState(null);
  const [imagePreviewA, setImagePreviewA] = useState(null);
  const [imageB, setImageB] = useState(null);
  const [imagePreviewB, setImagePreviewB] = useState(null);
  
  // Output
  const [isLoading, setIsLoading] = useState(false);
  const [loaderStep, setLoaderStep] = useState(0);
  const [loaderProgress, setLoaderProgress] = useState(0);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  
  const fileInputRef = useRef(null);
  const fileInputARef = useRef(null);
  const fileInputBRef = useRef(null);
  const loaderInterval = useRef(null);

  // Compress image to max 4MB (Anthropic limit is 5MB, leave margin)
  const compressImage = useCallback((file, maxSizeMB = 4) => {
    return new Promise((resolve) => {
      const img = new Image();
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      img.onload = () => {
        let { width, height } = img;
        const maxDimension = 2048; // Max dimension for good quality
        
        // Scale down if too large
        if (width > maxDimension || height > maxDimension) {
          const ratio = Math.min(maxDimension / width, maxDimension / height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }
        
        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);
        
        // Start with quality 0.9 and reduce if needed
        let quality = 0.9;
        let result = canvas.toDataURL('image/jpeg', quality);
        
        // Reduce quality until under maxSizeMB
        while (result.length > maxSizeMB * 1024 * 1024 * 1.37 && quality > 0.1) {
          quality -= 0.1;
          result = canvas.toDataURL('image/jpeg', quality);
        }
        
        // If still too large, reduce dimensions
        if (result.length > maxSizeMB * 1024 * 1024 * 1.37) {
          const scaleFactor = 0.7;
          canvas.width = Math.round(width * scaleFactor);
          canvas.height = Math.round(height * scaleFactor);
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          result = canvas.toDataURL('image/jpeg', 0.8);
        }
        
        resolve({
          dataUrl: result,
          base64: result.split(',')[1],
          mime_type: 'image/jpeg'
        });
      };
      
      img.src = URL.createObjectURL(file);
    });
  }, []);

  // Handle image upload with compression
  const handleImageUpload = useCallback(async (e, type = "single") => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    toast.info("Procesando imagen...");
    
    try {
      const compressed = await compressImage(file);
      
      if (type === "single") {
        setImage({ base64: compressed.base64, mime_type: compressed.mime_type });
        setImagePreview(compressed.dataUrl);
      } else if (type === "start") {
        setImageA({ base64: compressed.base64, mime_type: compressed.mime_type });
        setImagePreviewA(compressed.dataUrl);
      } else if (type === "end") {
        setImageB({ base64: compressed.base64, mime_type: compressed.mime_type });
        setImagePreviewB(compressed.dataUrl);
      }
      
      toast.success("Imagen lista");
    } catch (error) {
      console.error("Error processing image:", error);
      toast.error("Error al procesar imagen");
    }
  }, [compressImage]);

  // Clear images
  const clearImages = useCallback(() => {
    setImage(null);
    setImagePreview(null);
    setImageA(null);
    setImagePreviewA(null);
    setImageB(null);
    setImagePreviewB(null);
    setResult(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (fileInputARef.current) fileInputARef.current.value = "";
    if (fileInputBRef.current) fileInputBRef.current.value = "";
    toast.success("Imagenes limpiadas");
  }, []);

  // Start loader animation
  const startLoader = useCallback((currentMode) => {
    const steps = LOADER_STEPS[currentMode] || LOADER_STEPS.dialogue;
    setLoaderStep(0);
    setLoaderProgress(5);
    
    let step = 0;
    loaderInterval.current = setInterval(() => {
      step = Math.min(step + 1, steps.length - 1);
      setLoaderStep(step);
      setLoaderProgress(Math.min(5 + step * (85 / steps.length), 90));
    }, 950);
  }, []);

  // Stop loader
  const stopLoader = useCallback(() => {
    if (loaderInterval.current) {
      clearInterval(loaderInterval.current);
    }
    setLoaderProgress(100);
    setTimeout(() => {
      setIsLoading(false);
      setLoaderProgress(0);
    }, 300);
  }, []);

  // Generate prompt
  const handleGenerate = useCallback(async () => {
    // Validations
    if (mode === "dualframe") {
      if (!imageA || !imageB) {
        toast.error("Sube el Start Frame y el End Frame");
        return;
      }
    } else {
      if (!image) {
        toast.error("Sube la imagen primero");
        return;
      }
    }
    
    if (mode === "dialogue" && !hook.trim()) {
      toast.error("Escribe el texto del hook");
      return;
    }
    
    setIsLoading(true);
    startLoader(mode);
    
    try {
      const payload = {
        mode,
        hook: hook.trim(),
        context: context.trim(),
        accent,
        intensity,
        vfx,
        movement: selectedMovement,
        narration_style: narrationStyle ? {
          key: narrationStyle.id,
          desc: narrationStyle.styleDesc
        } : null,
        personality: personality ? {
          id: personality.id,
          name: personality.name,
          traits: personality.id === 'custom' ? customPersonality.trim() : personality.traits
        } : null,
        image: mode !== "dualframe" ? image : null,
        image_a: mode === "dualframe" ? imageA : null,
        image_b: mode === "dualframe" ? imageB : null,
        ai_provider: selectedAI.provider,
        ai_model: selectedAI.model,
      };
      
      const response = await axios.post(`${API}/generate-prompt`, payload);
      
      const resultData = {
        prompt: response.data.prompt,
        mode: response.data.mode,
        intensity: response.data.intensity,
        vfx: response.data.vfx,
        accent: response.data.accent,
      };
      
      setResult(resultData);
      
      // Save to history automatically
      try {
        await axios.post(`${API}/history`, {
          prompt: resultData.prompt,
          mode: resultData.mode,
          intensity: resultData.intensity,
          vfx: resultData.vfx,
          accent: resultData.accent,
          hook: hook.trim() || null,
          context: context.trim() || null,
          movement_name: selectedMovement?.name || null,
          narration_style: narrationStyle?.name || null,
        });
      } catch (historyError) {
        console.error("Error saving to history:", historyError);
      }
      
      toast.success("Prompt generado exitosamente");
    } catch (error) {
      console.error("Error generating prompt:", error);
      toast.error(error.response?.data?.detail || "Error al generar el prompt");
    } finally {
      stopLoader();
    }
  }, [mode, hook, context, accent, intensity, vfx, selectedMovement, narrationStyle, image, imageA, imageB, startLoader, stopLoader]);

  // Copy to clipboard with fallback
  const copyToClipboard = useCallback(() => {
    if (!result?.prompt) {
      toast.error("No hay prompt para copiar");
      return;
    }
    
    const textToCopy = result.prompt;
    
    // Try modern clipboard API first
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(textToCopy)
        .then(() => {
          setCopied(true);
          toast.success("Prompt copiado al portapapeles");
          setTimeout(() => setCopied(false), 2000);
        })
        .catch((err) => {
          console.error("Clipboard API failed:", err);
          fallbackCopy(textToCopy);
        });
    } else {
      // Fallback for older browsers or non-secure contexts
      fallbackCopy(textToCopy);
    }
  }, [result]);

  // Fallback copy method using textarea
  const fallbackCopy = useCallback((text) => {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.position = "fixed";
    textArea.style.left = "-999999px";
    textArea.style.top = "-999999px";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    try {
      const successful = document.execCommand("copy");
      if (successful) {
        setCopied(true);
        toast.success("Prompt copiado al portapapeles");
        setTimeout(() => setCopied(false), 2000);
      } else {
        toast.error("No se pudo copiar. Selecciona el texto manualmente.");
      }
    } catch (err) {
      console.error("Fallback copy failed:", err);
      toast.error("No se pudo copiar. Selecciona el texto manualmente.");
    }
    
    document.body.removeChild(textArea);
  }, []);

  // Enhance custom personality with AI
  const enhancePersonality = useCallback(async () => {
    if (!customPersonality.trim()) {
      toast.error("Escribe una descripción de personalidad primero");
      return;
    }
    
    setIsEnhancingPersonality(true);
    
    try {
      const response = await axios.post(`${API}/enhance-personality`, {
        description: customPersonality.trim(),
        ai_provider: selectedAI.provider,
        ai_model: selectedAI.model,
      });
      
      setCustomPersonality(response.data.enhanced_traits);
      toast.success("Personalidad enriquecida con IA");
    } catch (error) {
      console.error("Error enhancing personality:", error);
      toast.error(error.response?.data?.detail || "Error al enriquecer personalidad");
    } finally {
      setIsEnhancingPersonality(false);
    }
  }, [customPersonality, selectedAI]);

  // Approve and clear
  const approveAndClear = useCallback(() => {
    clearImages();
    setHook("");
    setContext("");
    toast.success("Prompt aprobado. Imagenes limpiadas");
  }, [clearImages]);

  // Load preset configuration
  const loadPreset = useCallback((preset) => {
    setMode(preset.mode);
    setAccent(preset.accent);
    setIntensity(preset.intensity);
    setVfx(preset.vfx);
    
    if (preset.movement_id) {
      setSelectedMovement({
        id: preset.movement_id,
        name: preset.movement_name,
        desc: preset.movement_desc
      });
    } else {
      setSelectedMovement(null);
    }
    
    if (preset.narration_style_id) {
      const style = NARRATION_STYLES.find(s => s.id === preset.narration_style_id);
      if (style) setNarrationStyle(style);
    }
  }, []);

  // Load history prompt to output
  const loadHistoryPrompt = useCallback((historyItem) => {
    setResult({
      prompt: historyItem.prompt,
      mode: historyItem.mode,
      intensity: historyItem.intensity,
      vfx: historyItem.vfx,
      accent: historyItem.accent,
    });
    
    // Also load the config
    setMode(historyItem.mode);
    setIntensity(historyItem.intensity);
    setVfx(historyItem.vfx);
    if (historyItem.accent) {
      setAccent(historyItem.accent === "guatemalteco" ? "guatemalan" : "mexican");
    }
    if (historyItem.hook) setHook(historyItem.hook);
    if (historyItem.context) setContext(historyItem.context);
    
    toast.success("Prompt cargado desde historial");
  }, []);

  // Current config for preset saving
  const currentConfig = {
    mode,
    accent,
    intensity,
    vfx,
    movement: selectedMovement,
    narrationStyle
  };

  // Get mode info
  const getModeInfo = useCallback(() => {
    const modeData = MODES.find(m => m.id === mode);
    return {
      label: modeData?.name || "DIALOGO",
      pillClass: mode === "dialogue" ? "dialogue" : 
                 mode === "silent" || mode === "detail" ? "silent" : "environmental"
    };
  }, [mode]);

  // Extract movement and atmosphere from prompt
  const extractPromptInfo = useCallback((prompt) => {
    if (!prompt) return { movement: "Ver prompt", atmosphere: "Ver prompt" };
    
    const movMatch = prompt.match(/(?:dolly|zoom|push|pull|handheld|drift|crash|snap|whip|drone|aerial)[^,.]{0,50}/i);
    const atmMatch = prompt.match(/(?:bokeh|flare|haze|grain|particle|anamorphic|mist|blur)[^,.]{0,50}/i);
    
    return {
      movement: movMatch ? movMatch[0].trim() + "..." : "Ver prompt",
      atmosphere: atmMatch ? atmMatch[0].trim() + "..." : "Ver prompt"
    };
  }, []);

  const loaderSteps = LOADER_STEPS[mode] || LOADER_STEPS.dialogue;
  const contextConfig = CONTEXT_CONFIG[mode] || CONTEXT_CONFIG.silent;
  const modeInfo = getModeInfo();
  const promptInfo = result ? extractPromptInfo(result.prompt) : null;

  // Check if generate button should be disabled
  const isGenerateDisabled = useCallback(() => {
    if (isLoading) return true;
    if (mode === "dualframe") {
      return !imageA || !imageB;
    }
    if (!image) return true;
    if (mode === "dialogue" && !hook.trim()) return true;
    return false;
  }, [isLoading, mode, image, imageA, imageB, hook]);

  return (
    <div className="shot-app" data-testid="shot-engine-app">
      {/* Header */}
      <header className="shot-header" data-testid="header">
        <div className="flex items-center gap-4">
          {/* SHOTS-GL Logo */}
          <img 
            src="https://customer-assets.emergentagent.com/job_motion-ai-studio-8/artifacts/3d6asbml_Asset%2010SHOTS.png" 
            alt="SHOTS-GL" 
            className="h-10 object-contain"
            data-testid="shots-logo"
          />
          <div className="h-6 w-px bg-[#262626] hidden sm:block" />
          {/* Grupo Locoff Logo */}
          <img 
            src="https://customer-assets.emergentagent.com/job_motion-ai-studio-8/artifacts/mhhci9ys_Asset%201Mlogo%20en%20blanco.png" 
            alt="Grupo Locoff" 
            className="h-8 object-contain opacity-60 hover:opacity-100 transition-opacity hidden sm:block"
            data-testid="grupolocoff-logo"
          />
        </div>
        <div className="flex items-center gap-3">
          {/* AI Model Selector */}
          <div className="flex items-center gap-1 bg-[#121212] border border-[#262626] rounded-md px-1 py-0.5" data-testid="ai-selector">
            {AI_MODELS.map((ai) => (
              <button
                key={ai.id}
                onClick={() => setSelectedAI(ai)}
                className={`font-mono text-[9px] px-2 py-1 rounded transition-colors ${
                  selectedAI.id === ai.id 
                    ? 'text-black font-medium' 
                    : 'text-[#737373] hover:text-white'
                }`}
                style={{ 
                  backgroundColor: selectedAI.id === ai.id ? ai.color : 'transparent'
                }}
                data-testid={`ai-option-${ai.id}`}
              >
                {ai.name}
              </button>
            ))}
          </div>
          <div className="h-4 w-px bg-[#262626] hidden sm:block" />
          <HistoryPresetDrawer 
            onLoadPreset={loadPreset}
            onLoadHistoryPrompt={loadHistoryPrompt}
            currentConfig={currentConfig}
          />
          <div className="h-4 w-px bg-[#262626] hidden sm:block" />
          <AuthButton 
            user={user}
            onLogout={onLogout}
          />
          <div className="font-mono text-[10px] tracking-widest text-[#F97316] border border-[#F97316]/30 px-3 py-1 rounded-full uppercase hidden sm:block">
            v4
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="shot-main">
        {/* Left Panel - Controls */}
        <ScrollArea className="shot-panel shot-panel-left" data-testid="controls-panel">
          <div className="flex flex-col gap-5 pb-4">
            
            {/* 01 - Image Upload */}
            <section>
              <div className="shot-section-label" data-testid="image-section-label">
                01 — {mode === "dualframe" ? "Start Frame + End Frame" : "Imagen del plano"}
              </div>
              
              {mode !== "dualframe" ? (
                <div
                  className={`shot-img-zone ${imagePreview ? "loaded" : ""}`}
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="image-upload-zone"
                >
                  {imagePreview ? (
                    <>
                      <img src={imagePreview} alt="Preview" />
                      <div className="shot-img-overlay">
                        <span className="font-mono text-[10px] tracking-widest text-white uppercase">Cambiar</span>
                      </div>
                    </>
                  ) : (
                    <>
                      <Upload className="w-8 h-8 text-[#404040]" />
                      <span className="font-mono text-[10px] tracking-widest text-[#737373] uppercase">Sube la imagen</span>
                      <span className="font-mono text-[8px] text-[#404040]">JPG · PNG · WEBP</span>
                    </>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <div className="font-mono text-[8px] tracking-widest text-[#F97316] uppercase mb-2">START FRAME</div>
                    <div
                      className={`shot-img-zone ${imagePreviewA ? "loaded" : ""}`}
                      style={{ maxHeight: "140px" }}
                      onClick={() => fileInputARef.current?.click()}
                      data-testid="image-upload-zone-start"
                    >
                      {imagePreviewA ? (
                        <>
                          <img src={imagePreviewA} alt="Start" />
                          <div className="shot-img-overlay">
                            <span className="font-mono text-[9px] text-white uppercase">Cambiar</span>
                          </div>
                        </>
                      ) : (
                        <>
                          <Upload className="w-6 h-6 text-[#404040]" />
                          <span className="font-mono text-[9px] text-[#737373] uppercase">Inicio</span>
                        </>
                      )}
                    </div>
                  </div>
                  <div>
                    <div className="font-mono text-[8px] tracking-widest text-[#BEF264] uppercase mb-2">END FRAME</div>
                    <div
                      className={`shot-img-zone ${imagePreviewB ? "loaded" : ""}`}
                      style={{ maxHeight: "140px" }}
                      onClick={() => fileInputBRef.current?.click()}
                      data-testid="image-upload-zone-end"
                    >
                      {imagePreviewB ? (
                        <>
                          <img src={imagePreviewB} alt="End" />
                          <div className="shot-img-overlay">
                            <span className="font-mono text-[9px] text-white uppercase">Cambiar</span>
                          </div>
                        </>
                      ) : (
                        <>
                          <Upload className="w-6 h-6 text-[#404040]" />
                          <span className="font-mono text-[9px] text-[#737373] uppercase">Final</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )}
              
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={(e) => handleImageUpload(e, "single")}
                className="hidden"
                data-testid="file-input-single"
              />
              <input
                ref={fileInputARef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={(e) => handleImageUpload(e, "start")}
                className="hidden"
                data-testid="file-input-start"
              />
              <input
                ref={fileInputBRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={(e) => handleImageUpload(e, "end")}
                className="hidden"
                data-testid="file-input-end"
              />
              
              {mode === "dualframe" && (
                <div className="font-mono text-[9px] text-[#404040] mt-2 pl-2 border-l-2 border-[#F97316] bg-[#121212] py-1 rounded-r">
                  Higgsfield interpolara el movimiento automaticamente.
                </div>
              )}
            </section>

            {/* 02 - Mode Selection */}
            <section>
              <div className="shot-section-label">02 — Tipo de toma</div>
              <div className="grid grid-cols-3 gap-2" data-testid="mode-selector">
                {MODES.map((m) => {
                  const IconComponent = iconMap[m.icon];
                  return (
                    <div
                      key={m.id}
                      className={`shot-mode-opt ${mode === m.id ? "active" : ""} ${m.id === "dualframe" ? "col-span-3" : ""}`}
                      onClick={() => setMode(m.id)}
                      data-testid={`mode-option-${m.id}`}
                    >
                      {IconComponent && <IconComponent className="w-4 h-4 flex-shrink-0" />}
                      <div>
                        <span className="mode-name font-display text-xs tracking-wider block">{m.name}</span>
                        <span className="font-mono text-[8px] text-[#404040] block mt-0.5">{m.desc}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>

            {/* 02b - Character Personality (for modes with characters) */}
            {(mode === "dialogue" || mode === "silent") && (
              <section data-testid="personality-section">
                <div className="shot-section-label">
                  <User className="w-3 h-3 inline mr-1" />
                  02b — Alma del personaje <span className="text-[#BEF264]">(NUEVO)</span>
                </div>
                <div className="grid grid-cols-2 gap-2 mb-2">
                  {PERSONALITY_ARCHETYPES.map((p) => {
                    const IconComponent = iconMap[p.icon];
                    return (
                      <div
                        key={p.id}
                        className={`shot-style-opt ${personality?.id === p.id ? "active" : ""} ${p.id === "custom" ? "col-span-2 border-dashed" : ""}`}
                        onClick={() => {
                          setPersonality(p);
                          if (p.id !== 'custom') {
                            setCustomPersonality("");
                          }
                        }}
                        data-testid={`personality-${p.id}`}
                      >
                        {IconComponent && <IconComponent className="w-4 h-4 flex-shrink-0" />}
                        <div>
                          <span className="style-name font-display text-[11px] tracking-wider block">{p.name}</span>
                          <span className="font-mono text-[8px] text-[#404040] block">{p.desc}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
                
                {/* Show traits preview for selected archetype */}
                {personality && personality.id !== 'custom' && (
                  <div className="font-mono text-[9px] text-[#737373] mt-2 pl-2 border-l-2 border-[#BEF264] bg-[#121212] py-2 rounded-r">
                    <span className="text-[#BEF264] uppercase text-[8px] block mb-1">Rasgos:</span>
                    {personality.traits}
                  </div>
                )}
                
                {/* Custom personality input */}
                {personality?.id === 'custom' && (
                  <div className="mt-2">
                    <div className="shot-hook-wrap">
                      <Textarea
                        value={customPersonality}
                        onChange={(e) => setCustomPersonality(e.target.value)}
                        placeholder="Describe la personalidad: cómo se para, cómo gesticula, si es tímido o extrovertido, si sonríe mucho o es serio, su energía general..."
                        className="shot-textarea min-h-[80px]"
                        data-testid="custom-personality-textarea"
                      />
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-2 text-[10px] h-7 border-[#BEF264] text-[#BEF264] hover:bg-[#BEF264]/10"
                      onClick={enhancePersonality}
                      disabled={isEnhancingPersonality || !customPersonality.trim()}
                      data-testid="enhance-personality-btn"
                    >
                      <Sparkles className="w-3 h-3 mr-1" />
                      {isEnhancingPersonality ? "ENRIQUECIENDO..." : "ENRIQUECER CON IA"}
                    </Button>
                    <span className="font-mono text-[8px] text-[#404040] ml-2">
                      La IA expandirá tu descripción con detalles de expresión corporal
                    </span>
                  </div>
                )}
                
                {/* Clear personality button */}
                {personality && (
                  <button
                    className="font-mono text-[9px] text-[#737373] hover:text-[#F97316] mt-2 underline"
                    onClick={() => {
                      setPersonality(null);
                      setCustomPersonality("");
                    }}
                    data-testid="clear-personality-btn"
                  >
                    Limpiar personalidad
                  </button>
                )}
              </section>
            )}

            {/* 03 - Hook (dialogue mode) */}
            {mode === "dialogue" && (
              <section data-testid="hook-section">
                <div className="shot-section-label">03 — Texto del hook</div>
                <div className="shot-hook-wrap">
                  <Textarea
                    value={hook}
                    onChange={(e) => setHook(e.target.value)}
                    maxLength={300}
                    placeholder="Ej: Las deudas me sacaron la roja, y yo ya no tengo ni para el arbitraje!"
                    className="shot-textarea min-h-[80px]"
                    data-testid="hook-textarea"
                  />
                  <span className="shot-char-count">{hook.length}/300</span>
                </div>
              </section>
            )}

            {/* 03b - Narration Style (dialogue mode) */}
            {mode === "dialogue" && (
              <section data-testid="narration-style-section">
                <div className="shot-section-label">03b — Estilo de narracion</div>
                <div className="grid grid-cols-2 gap-2">
                  {NARRATION_STYLES.map((style) => {
                    const IconComponent = iconMap[style.icon];
                    return (
                      <div
                        key={style.id}
                        className={`shot-style-opt ${narrationStyle?.id === style.id ? "active" : ""}`}
                        onClick={() => setNarrationStyle(style)}
                        data-testid={`narration-style-${style.id}`}
                      >
                        {IconComponent && <IconComponent className="w-4 h-4 flex-shrink-0" />}
                        <div>
                          <span className="style-name font-display text-[11px] tracking-wider block">{style.name}</span>
                          <span className="font-mono text-[8px] text-[#404040] block">{style.desc}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            {/* 03 - Context (non-dialogue modes) */}
            {mode !== "dialogue" && (
              <section data-testid="context-section">
                <div className="shot-section-label">
                  {contextConfig.label} <span className="text-[#404040] text-[8px]">(opcional)</span>
                </div>
                <Textarea
                  value={context}
                  onChange={(e) => setContext(e.target.value)}
                  placeholder={contextConfig.placeholder}
                  className="shot-textarea shot-context-textarea"
                  data-testid="context-textarea"
                />
              </section>
            )}

            {/* 04 - Accent (dialogue mode) */}
            {mode === "dialogue" && (
              <section data-testid="accent-section">
                <div className="shot-section-label">04 — Acento</div>
                <div className="flex gap-2">
                  {ACCENTS.map((acc) => (
                    <div
                      key={acc.id}
                      className={`shot-accent-opt ${accent === acc.id ? "active" : ""}`}
                      onClick={() => setAccent(acc.id)}
                      data-testid={`accent-option-${acc.id}`}
                    >
                      <span className="accent-value font-display text-base block">{acc.flag === "GT" ? "GT" : "MX"}</span>
                      <span className="font-mono text-[8px] text-[#404040] uppercase block mt-1">{acc.label}</span>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* 05 - Intensity */}
            <section data-testid="intensity-section">
              <div className="shot-section-label">{mode === "dialogue" ? "05" : "04"} — Intensidad cinematica</div>
              <div className="flex gap-2">
                {INTENSITIES.map((int) => (
                  <div
                    key={int.id}
                    className={`shot-int-opt ${intensity === int.id ? "active" : ""}`}
                    onClick={() => setIntensity(int.id)}
                    data-testid={`intensity-option-${int.id}`}
                  >
                    <span className="int-name font-display text-sm tracking-wider block">{int.name}</span>
                    <span className="font-mono text-[8px] text-[#404040] block mt-1">{int.desc}</span>
                  </div>
                ))}
              </div>
            </section>

            {/* 06 - Camera Movement */}
            <section data-testid="movement-section">
              <div className="shot-section-label">{mode === "dialogue" ? "06" : "05"} — Movimiento de camara</div>
              
              <div className="flex items-center justify-between mb-2">
                <span className="font-mono text-[9px] text-[#404040]">La IA sugiere automaticamente. Puedes cambiarlo.</span>
                <button
                  className={`shot-auto-btn ${!selectedMovement ? "active" : "inactive"}`}
                  onClick={() => setSelectedMovement(null)}
                  data-testid="auto-movement-btn"
                >
                  {!selectedMovement ? "● AUTO" : "AUTO"}
                </button>
              </div>
              
              <Accordion type="single" collapsible className="w-full">
                <AccordionItem value="movements" className="border-[#262626]">
                  <AccordionTrigger className="font-mono text-[10px] text-[#737373] py-2 hover:no-underline hover:text-[#A3A3A3]">
                    {selectedMovement ? `Seleccionado: ${selectedMovement.name}` : "Explorar movimientos"}
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="flex flex-wrap gap-1 mb-3">
                      {MOVEMENT_CATEGORIES.map((cat) => (
                        <button
                          key={cat.id}
                          className={`shot-mv-tab ${movementCategory === cat.id ? "active" : ""}`}
                          onClick={() => setMovementCategory(cat.id)}
                          data-testid={`movement-category-${cat.id}`}
                        >
                          {cat.name}
                        </button>
                      ))}
                    </div>
                    
                    <div className="grid grid-cols-3 gap-1 max-h-[140px] overflow-y-auto pr-1">
                      {MOVEMENTS[movementCategory]?.map((mv) => (
                        <button
                          key={mv.id}
                          className={`shot-mv-pill ${selectedMovement?.id === mv.id ? "active" : ""}`}
                          onClick={() => setSelectedMovement(mv)}
                          title={mv.desc}
                          data-testid={`movement-option-${mv.id}`}
                        >
                          {mv.name}
                        </button>
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
              
              {selectedMovement && (
                <div className="shot-mv-selected">
                  <span className="font-mono text-[8px] text-[#404040] tracking-wider uppercase">SELECCIONADO: </span>
                  <span className="font-mono text-[11px] text-[#3B82F6]">{selectedMovement.name}</span>
                  <span className="font-mono text-[9px] text-[#404040] block mt-1">{selectedMovement.desc}</span>
                </div>
              )}
            </section>

            {/* 07 - VFX */}
            <section data-testid="vfx-section">
              <div className="shot-section-label">{mode === "dialogue" ? "07" : "06"} — Efectos visuales (VFX)</div>
              <div className="flex gap-2">
                <div
                  className={`shot-vfx-opt ${vfx === "off" ? "active" : ""}`}
                  style={{ flex: 1.5 }}
                  onClick={() => setVfx("off")}
                  data-testid="vfx-off"
                >
                  <span className="vfx-name font-display text-sm block">VFX OFF</span>
                  <span className="font-mono text-[8px] text-[#404040] block mt-1">Fotorrealista</span>
                </div>
                <div
                  className={`shot-vfx-opt ${vfx === "on" ? "active" : ""}`}
                  onClick={() => setVfx("on")}
                  data-testid="vfx-on"
                >
                  <span className="vfx-name font-display text-sm block">VFX ON</span>
                  <span className="font-mono text-[8px] text-[#404040] block mt-1">Con efectos</span>
                </div>
              </div>
              <div className="font-mono text-[9px] text-[#404040] mt-2 pl-2 border-l-2 border-[#404040] bg-[#121212] py-1 rounded-r">
                {vfx === "off" 
                  ? "OFF: sin hexagonos, speed lines ni efectos de comic."
                  : "ON: Higgsfield puede agregar efectos visuales."}
              </div>
            </section>

            {/* Generate Button */}
            <Button
              className="shot-gen-btn"
              onClick={handleGenerate}
              disabled={isGenerateDisabled()}
              data-testid="generate-prompt-btn"
            >
              <Wand2 className="w-5 h-5 mr-2 inline" />
              {isLoading ? "GENERANDO..." : "ANALIZAR Y GENERAR"}
            </Button>
          </div>
        </ScrollArea>

        {/* Right Panel - Output */}
        <div className="shot-panel shot-panel-right" data-testid="output-panel">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h2 className={`shot-output-title ${result ? "ready" : ""}`} data-testid="output-title">
                {result ? "PROMPT LISTO" : "—"}
              </h2>
              <p className="font-mono text-[10px] tracking-widest text-[#404040] uppercase mt-1">
                {result 
                  ? `${modeInfo.label} · ${intensity.toUpperCase()} · VFX ${vfx.toUpperCase()}${mode === "dialogue" ? ` · ${result.accent}` : ""} · listo para Higgsfield`
                  : "Selecciona modo y genera"}
              </p>
            </div>
            {result && (
              <div className="flex gap-2">
                <button
                  className={`shot-copy-btn ${copied ? "copied" : ""}`}
                  onClick={copyToClipboard}
                  data-testid="copy-prompt-btn"
                >
                  {copied ? <><Check className="w-3 h-3 mr-1 inline" /> COPIADO</> : <><Copy className="w-3 h-3 mr-1 inline" /> COPIAR</>}
                </button>
                <button
                  className="shot-clear-btn"
                  onClick={approveAndClear}
                  data-testid="approve-clear-btn"
                >
                  <Trash2 className="w-3 h-3 mr-1 inline" /> APROBAR Y LIMPIAR
                </button>
              </div>
            )}
          </div>

          {result ? (
            <>
              {/* Analysis */}
              <div className="shot-analysis mb-4" data-testid="analysis-section">
                <div className="shot-analysis-item">
                  <span className="shot-analysis-key">Modo</span>
                  <span className={`shot-mode-pill ${modeInfo.pillClass}`}>{modeInfo.label}</span>
                </div>
                <div className="shot-analysis-item">
                  <span className="shot-analysis-key">Plano</span>
                  <span className="shot-analysis-value highlight">Detectado automaticamente</span>
                </div>
                <div className="shot-analysis-item">
                  <span className="shot-analysis-key">Movimiento</span>
                  <span className="shot-analysis-value highlight">{promptInfo?.movement}</span>
                </div>
                <div className="shot-analysis-item">
                  <span className="shot-analysis-key">Atmosfera</span>
                  <span className="shot-analysis-value">{promptInfo?.atmosphere}</span>
                </div>
              </div>

              {/* Prompt Box */}
              <div className="shot-prompt-box" data-testid="prompt-box">
                <div className="shot-prompt-header">
                  <span className="font-mono text-[11px] text-[#737373]">
                    prompt_<span className="text-[#BEF264]">{mode}</span>.txt
                  </span>
                  <button
                    className={`shot-copy-btn ${copied ? "copied" : ""}`}
                    onClick={copyToClipboard}
                    data-testid="prompt-copy-btn"
                  >
                    {copied ? "COPIADO" : "COPIAR"}
                  </button>
                </div>
                <textarea
                  className="shot-prompt-textarea"
                  value={result.prompt}
                  readOnly
                  data-testid="prompt-textarea"
                />
              </div>
            </>
          ) : (
            <div className="shot-empty-state" data-testid="empty-state">
              <div className="shot-empty-bg" />
              <div className="flex items-center gap-3 opacity-30 relative z-10">
                <div className="px-3 py-2 border border-[#404040] rounded font-mono text-[10px] tracking-widest text-[#404040] uppercase">
                  Imagen
                </div>
                <span className="text-[#404040]">→</span>
                <div className="px-3 py-2 border border-[#404040] rounded font-mono text-[10px] tracking-widest text-[#404040] uppercase">
                  Modo
                </div>
                <span className="text-[#404040]">→</span>
                <div className="px-3 py-2 border border-[#BEF264] rounded font-mono text-[10px] tracking-widest text-[#BEF264] uppercase">
                  Prompt
                </div>
              </div>
              <p className="font-mono text-[11px] tracking-widest text-[#404040] uppercase text-center relative z-10">
                Dialogo · Silencio · Detalle<br />
                Ambiente · Dos Frames
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Loader Overlay */}
      {isLoading && (
        <div className="shot-loader-wrap" data-testid="loader-overlay">
          <div className="shot-loader-ring" />
          <div className="shot-loader-status">
            {loaderSteps[loaderStep]}
          </div>
          <div className="shot-loader-bar">
            <div 
              className="shot-loader-fill" 
              style={{ width: `${loaderProgress}%` }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
